import { useCallback, useEffect, useState } from "react";
import { makeStyles } from "tss-react/mui";
import { MiIcon } from "@miplatform/components";
import { toastService } from "@miplatform/toast";
import { mdiLockOutline } from "@mdi/js";
import { useSession } from "@mibuildweb/hooks";
import { Box, Typography } from "@mui/material";
import {
  CalendarLockProvider,
  useCalendarContext,
  useCalendarLockContext,
} from "../../../providers";
import { WorkOrderAttemptDrawer } from "../WorkOrderAttemptDrawer/WorkOrderAttemptDrawer";
import { AssignmentView } from "./AssignmentView";
import {
  CalendarVerticalTabs,
  type TabGroupItem,
} from "./CalendarVerticalTabs";
import { SchedulerView } from "./SchedulerView";
import { useNHCPriorityScheduler } from "./useNHCPriorityScheduler";
import {
  DEFAULT_GROUP,
  SchedulerViewType,
  TabGroupKey,
  useNHCPrioritySchedulerTabs,
} from "./useNHCPrioritySchedulerTabs";

export function NHCPriorityScheduler() {
  return (
    <CalendarLockProvider>
      <NHCPrioritySchedulerContent />
    </CalendarLockProvider>
  );
}

function CalendarLockBanner() {
  const { classes } = useStyles();
  const { isCalendarLocked, lockDetails } = useCalendarLockContext();

  if (!isCalendarLocked) {
    return null;
  }

  toastService.error({ text: lockDetails?.message ?? "" });

  return (
    <Box className={classes.lockTextContainer}>
      <MiIcon path={mdiLockOutline} size={0.75} />
      <Typography variant="body2">{lockDetails?.message}</Typography>
    </Box>
  );
}

export function NHCPrioritySchedulerContent() {
  const { classes } = useStyles();
  const { session } = useSession();
  const [activeGroup, setActiveGroup] = useState<TabGroupKey>(DEFAULT_GROUP);

  const {
    events,
    calendarSet,
    assignableUsers,
    schedulerUsers,
    selectedAttempt,
    setSelectedAttempt,
    setVerticalTabActiveFilter,
  } = useCalendarContext();
  const { isCalendarLocked, createCalendarLockRenewal } =
    useCalendarLockContext();
  const scheduler = useNHCPriorityScheduler();
  const workTemplateBases = calendarSet?.config?.workTemplateBases ?? [];
  const tabsWithItems = useNHCPrioritySchedulerTabs(
    workTemplateBases,
    events,
    scheduler.rawUnassignedWorkOrders,
    isCalendarLocked
  );

  const isAssignee =
    !!session?.userId &&
    assignableUsers?.some((user) => user.id === session.userId);

  const isScheduler =
    !!session?.userId &&
    schedulerUsers?.some((user) => user.id === session.userId);

  const assigneeMode = isAssignee && !isScheduler;
  const assigneeId = assigneeMode ? session?.userId : null;
  const assigneeModeUser = assignableUsers?.find(
    (user) => user.id === assigneeId
  );

  const handleSelectionChange = useCallback((group: TabGroupKey) => {
    setActiveGroup(group);
    setVerticalTabActiveFilter(null);
  }, []);

  const handleSubItemPress = useCallback(
    (_group: TabGroupKey, _item: TabGroupItem) => {
      setVerticalTabActiveFilter(_item.value);
    },
    []
  );

  const activeView = assigneeMode
    ? SchedulerViewType.Scheduler
    : tabsWithItems[activeGroup].view;

  useEffect(() => {
    const onLockFailure = () => {
      setActiveGroup(DEFAULT_GROUP);
    };
    if (activeGroup !== DEFAULT_GROUP) {
      const cleanup = createCalendarLockRenewal(onLockFailure);
      return cleanup;
    }
    return undefined;
  }, [activeGroup]);

  return (
    <>
      <CalendarLockBanner />
      <Box className={classes.container}>
        {!assigneeMode && (
          <CalendarVerticalTabs
            groups={tabsWithItems}
            defaultGroup={activeGroup}
            onSelectionChange={handleSelectionChange}
            onSubItemPress={handleSubItemPress}
          />
        )}
        <Box className={classes.mainView}>
          {activeView === SchedulerViewType.Assignment && (
            <AssignmentView scheduler={scheduler} />
          )}
          {activeView === SchedulerViewType.Scheduler && (
            <SchedulerView assigneeModeUser={assigneeModeUser} />
          )}
          {selectedAttempt && (
            <WorkOrderAttemptDrawer
              key={selectedAttempt.id}
              open={!!selectedAttempt}
              listAttempt={selectedAttempt}
              onClose={() => setSelectedAttempt(null)}
            />
          )}
        </Box>
      </Box>
    </>
  );
}

const useStyles = makeStyles()((theme) => ({
  container: {
    display: "flex",
    padding: theme.spacing(1),
    height: "100%",
    width: "100%",
    gap: theme.spacing(2),
  },
  lockTextContainer: {
    display: "flex",
    flexDirection: "row",
    backgroundColor: theme.palette.warning.light,
    gap: theme.spacing(2),
    padding: 8,
  },
  mainView: {
    display: "flex",
    overflow: "auto",
    minHeight: 0,
    width: "100%",
  },
}));
